import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MoreHorizontal } from 'lucide-react';

import { isNavItemActive } from '../constants/navigationItems';

function buildGroups(items) {
  const order = [];
  const map = new Map();
  items.forEach((item) => {
    const key = item.group || '';
    if (!map.has(key)) {
      map.set(key, { label: item.groupLabel || null, items: [] });
      order.push(key);
    }
    map.get(key).items.push(item);
  });
  return order.map((key) => ({ key, ...map.get(key) }));
}

function renderMenuItems(items, location, onItemClick, setOpen) {
  const groups = buildGroups(items);
  const hasGroupHeaders = groups.some((g) => g.label);

  return groups.map((group, gIdx) => (
    <React.Fragment key={group.key || `__group_${gIdx}`}>
      {hasGroupHeaders && gIdx > 0 && <div className="nav-more-menu__divider" />}
      {group.label && <div className="nav-more-menu__group-label">{group.label}</div>}
      {group.items.map((item) => {
        const Icon = item.icon;
        const active = isNavItemActive(item.path, location);
        return (
          <Link
            key={item.id}
            to={item.path}
            role="menuitem"
            className={`nav-more-menu__item ${active ? 'is-active' : ''}`}
            title={item.description || item.label}
            onClick={() => {
              onItemClick(item);
              setOpen(false);
            }}
          >
            <Icon size={16} />
            <span className="nav-more-menu__item-label">{item.label}</span>
          </Link>
        );
      })}
    </React.Fragment>
  ));
}

/**
 * Dropdown listing navigation items (typically unused / low-traffic).
 */
export function NavMoreMenu({
  items = [],
  location,
  onItemClick = () => {},
  variant = 'header',
  label = 'More',
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  if (!items.length) return null;

  const isHeader = variant === 'header';

  return (
    <div className={`nav-more-menu nav-more-menu--${variant}`} ref={rootRef}>
      <button
        type="button"
        className={
          isHeader
            ? 'btn btn-outline-secondary btn-sm nav-more-menu__trigger'
            : 'btn btn-sm btn-outline-secondary w-100 nav-more-menu__trigger-sidebar'
        }
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-haspopup="menu"
        title={`${label} (${items.length})`}
      >
        <MoreHorizontal size={isHeader ? 16 : 18} />
        <span className={isHeader ? 'd-none d-md-inline ms-1' : 'ms-2'}>{label}</span>
        <span className="badge rounded-pill bg-secondary-subtle text-secondary ms-1">
          {items.length}
        </span>
      </button>
      {open && (
        <div
          className={`nav-more-menu__panel ${isHeader ? 'nav-more-menu__panel--header' : 'nav-more-menu__panel--sidebar'}`}
          role="menu"
        >
          {renderMenuItems(items, location, onItemClick, setOpen)}
        </div>
      )}
      <style>{`
        .nav-more-menu {
          position: relative;
        }
        .nav-more-menu__trigger-sidebar {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.25rem;
        }
        .nav-more-menu__panel {
          position: absolute;
          z-index: 1102;
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 0.5rem;
          box-shadow: 0 12px 28px rgba(15, 23, 42, 0.12);
          max-height: min(70vh, 420px);
          overflow-y: auto;
          min-width: 220px;
        }
        .nav-more-menu__panel--header {
          top: calc(100% + 6px);
          left: 0;
        }
        .nav-more-menu__panel--sidebar {
          top: calc(100% + 6px);
          left: 0;
          right: 0;
          min-width: 0;
        }
        .nav-more-menu__item {
          display: flex;
          align-items: center;
          gap: 0.65rem;
          padding: 0.55rem 0.85rem;
          color: #1e293b;
          text-decoration: none;
          font-size: 0.875rem;
        }
        .nav-more-menu__item:hover {
          background: #f8fafc;
        }
        .nav-more-menu__item.is-active {
          background: rgba(13, 110, 253, 0.1);
          color: #0d6efd;
          font-weight: 600;
        }
        .nav-more-menu__item-label {
          flex: 1;
          min-width: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .nav-more-menu__group-label {
          padding: 0.45rem 0.85rem 0.2rem;
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.07em;
          text-transform: uppercase;
          color: #64748b;
        }
        .nav-more-menu__divider {
          height: 1px;
          background: #e2e8f0;
          margin: 0.35rem 0;
        }
      `}</style>
    </div>
  );
}
